import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import BrandedPreviewShell from '../previews/BrandedPreviewShell';
import { brooksBrandProfile } from '../../branding/companyBrand';

const ratingOptions = [
  { label: 'Poor', value: 1 },
  { label: 'Okay', value: 2 },
  { label: 'Good', value: 3 },
  { label: 'Great', value: 4 },
];

const ReviewRequestPreview: React.FC = () => {
  const brand = brooksBrandProfile;
  const [rating, setRating] = useState(4);

  return (
    <BrandedPreviewShell brand={brand} previewLabel="Review request">
      <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.25}>
          <Stack spacing={0.75}>
            <Typography variant="h4" sx={{ color: 'var(--cx-brand-text)', fontSize: { xs: 24, sm: 26 } }}>
              How was your service?
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', lineHeight: 1.6 }}>
              {brand.customerName}, tell us how {brand.technicianName} did on today's visit.
            </Typography>
          </Stack>

          <Box
            sx={{
              bgcolor: 'var(--cx-brand-primary-soft)',
              border: '1px solid',
              borderColor: 'var(--cx-brand-border)',
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <CheckCircleRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 20 }} />
              <Typography variant="subtitle2">Quarterly pest control service</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)', display: 'block', mt: 0.5 }}>
              Completed by {brand.technicianName}
            </Typography>
          </Box>

          <Divider />

          <Stack spacing={1.25}>
            <Typography variant="subtitle2">Rate your experience</Typography>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              {ratingOptions.map((option) => {
                const selected = rating === option.value;

                return (
                  <Button
                    aria-pressed={selected}
                    key={option.value}
                    onClick={() => setRating(option.value)}
                    sx={{
                      bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-100)',
                      border: '1px solid',
                      borderColor: selected ? 'var(--cx-brand-primary)' : 'transparent',
                      color: selected ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text)',
                      flexDirection: 'column',
                      fontWeight: 800,
                      gap: 0.25,
                      height: 70,
                      minHeight: 70,
                      px: 0.5,
                      '&:hover': {
                        bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-200)',
                        borderColor: 'var(--cx-brand-primary)',
                      },
                    }}
                    type="button"
                    variant="outlined"
                  >
                    <Typography component="span" sx={{ color: 'inherit', fontSize: 20, fontWeight: 900 }}>
                      {option.value}
                    </Typography>
                    <Typography component="span" sx={{ color: 'inherit', fontSize: 11, fontWeight: 700 }}>
                      {option.label}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
          </Stack>

          <Button
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              bgcolor: 'var(--cx-brand-primary)',
              color: 'var(--color-base-white)',
              '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
            }}
            variant="contained"
          >
            Submit feedback
          </Button>

          <Chip
            label={`${brand.domain}/code`}
            sx={{
              alignSelf: 'center',
              bgcolor: 'var(--color-gray-100)',
              color: 'var(--cx-brand-text-muted)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          />
        </Stack>
      </Card>
    </BrandedPreviewShell>
  );
};

export default ReviewRequestPreview;
